import React, { useState, useEffect } from "react";
import { orders, filterByVb, filterByOrder } from "../../utils/orders";
import { FiExternalLink } from "react-icons/fi";
import { getPaginatedResults, orderByDate } from "../../utils/sortFunctions";
import PaginationNav from "../PaginationNav/PaginationNav";
import SortControls from "../SortControls/SortControls";
import PillSortButton from "../PillInput/PillSortButton";
import Modal from "../Modal/Modal";
import Button from "../Button/Button";
import { FaFilter } from "react-icons/fa";
import PillInput from "../PillInput/PillInput";
import AutoCompleteInput from "../AutoCompleteInput/AutoCompleteInput";

const consolidatedList = orderByDate(
  [...orders].map((entry) => {
    entry.sortDate = entry.date ? entry.date : "";
    return entry;
  }),
  "sortDate"
);

const uniqueVbs = ["All", ...new Set(orders.map((order) => order.vb))];
const uniqueOrders = ["All", ...new Set(orders.map((order) => order.order))];

const NOPtable = ({ ...props }) => {
  const [currentPage, setPage] = useState(1);
  const [searchOrderBy, setSearchOrderBy] = useState("sortDate");
  const [searchOrderDirection, setSearchOrderDirection] = useState("desc");
  const [filteredList, setFilteredList] = useState(consolidatedList);
  const [modalOpen, toggleModal] = useState(false);
  const [selectedVb, setSelectedVb] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const applySorting = () => {
    const sorted = [...consolidatedList].sort((a, b) => {
      if (searchOrderBy === "vb") {
        return searchOrderDirection === "asc"
          ? a.vb.localeCompare(b.vb)
          : b.vb.localeCompare(a.vb);
      } else if (searchOrderBy === "sortDate") {
        return searchOrderDirection === "asc"
          ? new Date(a.sortDate) - new Date(b.sortDate)
          : new Date(b.sortDate) - new Date(a.sortDate);
      } else if (searchOrderBy === "order") {
        return searchOrderDirection === "asc"
          ? a.order.localeCompare(b.order)
          : b.order.localeCompare(a.order);
      }
      return 0;
    });
    setFilteredList(sorted);
  };

  const applyFilters = (vbFilter, orderFilter) => {
    let filtered = consolidatedList;
    if (vbFilter && vbFilter !== "All") {
      filtered = filtered.filter((item) => item.vb.toLowerCase() === vbFilter.toLowerCase());
    }
    if (orderFilter && orderFilter !== "All") {
      filtered = filtered.filter((item) => item.order.toLowerCase() === orderFilter.toLowerCase());
    }
    setFilteredList(filtered);
  };

  const setListOrder = (filterId) => {
    if (filterId === searchOrderBy && searchOrderDirection === "asc") {
      setSearchOrderDirection("desc");
    } else {
      setSearchOrderBy(filterId);
      setSearchOrderDirection("asc");
    }
  };

  useEffect(() => {
    applySorting();
  }, [searchOrderBy, searchOrderDirection]);

  return (
    <>
      <div className="flexContainer mobileCol justifyBetween alignEnd">
        <SortControls>
          <PillSortButton
            id="sortDate"
            label="Date Issued"
            active={searchOrderBy === "sortDate"}
            order={searchOrderDirection}
            clickEvt={() => setListOrder("sortDate")}
          />
          <PillSortButton
            id="vb"
            label="Vendor/Builder"
            active={searchOrderBy === "vb"}
            order={searchOrderDirection}
            clickEvt={() => setListOrder("vb")}
          />
          <PillSortButton
            id="order"
            label="Notices/Orders"
            active={searchOrderBy === "order"}
            order={searchOrderDirection}
            clickEvt={() => setListOrder("order")}
          />
        </SortControls>

        <Button
          id="filterBtn"
          icon={<FaFilter />}
          size="sm"
          clickEvt={() => toggleModal(!modalOpen)}
          text="Search Result"
        />
      </div>

      {modalOpen && (
        <Modal closeEvt={() => toggleModal(!modalOpen)}>
          <div>
            <h2>Filter Options</h2>
            <div>
              <h4>Vendor/Builder</h4>
              <AutoCompleteInput
                options={uniqueVbs}
                selectedOption={selectedVb || "All"}
                onChange={(value) => {
                  setSelectedVb(value); // Update the selected option state
                  applyFilters(value, selectedOrder); // Apply filters based on the selected option
                }}
                value={selectedVb || "All"} // Ensure the value reflects the selected option
                placeholder="Select a Vendor/Builder"
              />
            </div>
            <div style={{ marginTop: `2rem` }}>
              <div style={{ marginBottom: `.5rem` }}>
                <span className="bold">Order</span>
              </div>
              <AutoCompleteInput
                options={uniqueOrders}
                selectedOption={selectedOrder || "All"}
                onChange={(value) => {
                  setSelectedOrder(value); // Update the selected option state
                  applyFilters(selectedVb, value); // Apply filters based on the selected option
                }}
                value={selectedOrder || "All"} // Ensure the value reflects the selected option
                placeholder="Select an Order"
              />
            </div>
          </div>
        </Modal>
      )}

      <table style={{ marginBottom: `2rem` }}>
        <thead>
          <tr>
            <th>Vendor/Builder</th>
            <th>Notice/Order Issued</th>
            <th>Date Issued</th>
          </tr>
        </thead>
        <tbody>
          {getPaginatedResults(filteredList, currentPage).map((order, i) => (
            <tr key={`${i}`}>
              <td>
                <a href={order.link} title={order.vb}>
                  <FiExternalLink
                    style={{
                      verticalAlign: "center",
                      marginRight: ".5rem",
                      fontSize: `1.5rem`,
                    }}
                  />
                  <span>{order.vb}</span>
                </a>
              </td>
              <td>
                {order.order}
                <br />
                <b>{order.note}</b>
              </td>
              <td>{order.date}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {filteredList.length > 10 && (
        <PaginationNav
          currentPage={currentPage}
          clickEvt={setPage}
          resultsLength={filteredList.length}
        />
      )}
    </>
  );
};

export default NOPtable;
